import React, { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";

function leadName(lead) {
  return lead?.full_name || lead?.fullName || lead?.leadName || lead?.name || "";
}

function companyName(lead) {
  return lead?.company_name || lead?.companyName || lead?.company || "";
}

function isValidUrl(value) {
  if (!value) return true;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

export default function DossierForm({ lead, initialDossier, busy, apiError, onSubmit }) {
  const defaults = useMemo(() => ({
    prospect_name: initialDossier?.prospect_name || leadName(lead),
    prospect_company: initialDossier?.prospect_company || companyName(lead),
    prospect_linkedin_url: initialDossier?.prospect_linkedin_url || lead?.linkedin_url || lead?.url || "",
    product_context: initialDossier?.product_context || "banao",
    other_product_name: initialDossier?.other_product_name || "",
    pasted_context: initialDossier?.pasted_context || "",
  }), [initialDossier, lead]);

  const [form, setForm] = useState(defaults);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setForm(defaults);
    setErrors({});
  }, [defaults]);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!form.prospect_name.trim() || form.prospect_name.trim().length < 2) next.prospect_name = "Enter At Least 2 Characters.";
    if (!form.prospect_company.trim() || form.prospect_company.trim().length < 2) next.prospect_company = "Enter At Least 2 Characters.";
    if (form.prospect_linkedin_url && !isValidUrl(form.prospect_linkedin_url)) next.prospect_linkedin_url = "Enter A Valid URL.";
    if (form.product_context === "other" && !form.other_product_name.trim()) next.other_product_name = "Required For Other.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    await onSubmit({
      ...form,
      prospect_name: form.prospect_name.trim(),
      prospect_company: form.prospect_company.trim(),
      prospect_linkedin_url: form.prospect_linkedin_url.trim(),
      other_product_name: form.other_product_name.trim(),
      pasted_context: form.pasted_context.trim(),
    });
  };

  return (
    <form className="dossier-form" onSubmit={submit}>
      {apiError && <div className="dossier-alert error">{apiError}</div>}

      <div className="dossier-form-grid">
        <label>
          Prospect Name
          <input disabled={busy} value={form.prospect_name} onChange={(event) => update("prospect_name", event.target.value)} />
          {errors.prospect_name && <span className="dossier-field-error">{errors.prospect_name}</span>}
        </label>

        <label>
          Prospect Company
          <input disabled={busy} value={form.prospect_company} onChange={(event) => update("prospect_company", event.target.value)} />
          {errors.prospect_company && <span className="dossier-field-error">{errors.prospect_company}</span>}
        </label>

        <label>
          LinkedIn URL
          <input disabled={busy} type="url" value={form.prospect_linkedin_url} onChange={(event) => update("prospect_linkedin_url", event.target.value)} />
          <span className="dossier-helper">Significantly Improves Dossier Depth</span>
          {errors.prospect_linkedin_url && <span className="dossier-field-error">{errors.prospect_linkedin_url}</span>}
        </label>

        <label>
          Product Context
          <select disabled={busy} value={form.product_context} onChange={(event) => update("product_context", event.target.value)}>
            <option value="interviewgod">InterviewGod / IG</option>
            <option value="banao">Banao</option>
            <option value="generic">Generic</option>
            <option value="other">Other</option>
          </select>
        </label>

        <label className={`dossier-other-product ${form.product_context === "other" ? "show" : ""}`}>
          Other Product Name
          <input disabled={busy} value={form.other_product_name} onChange={(event) => update("other_product_name", event.target.value)} />
          {errors.other_product_name && <span className="dossier-field-error">{errors.other_product_name}</span>}
        </label>
      </div>

      <label className="dossier-textarea-label">
        Additional Context (Optional)
        <textarea
          disabled={busy}
          rows={7}
          value={form.pasted_context}
          onChange={(event) => update("pasted_context", event.target.value)}
          placeholder="Paste LinkedIn Profile Text, Recent Posts, Emails, Or Notes..."
        />
        <span className="dossier-helper">Paste LinkedIn Profile Text, Recent Posts, Emails, Or Any Notes About This Prospect. The More You Paste, The Better The Dossier.</span>
      </label>

      <div className="dossier-form-footer">
        <button className="Primary-Button" type="submit" disabled={busy}>
          {busy ? <><Loader2 size={15} className="dossier-spin" /> Generating...</> : "Generate Dossier ->"}
        </button>
        <span>Takes 30-90 Seconds. We Search The Web Automatically - No Manual Research Needed.</span>
      </div>
    </form>
  );
}
